import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import Starfield from "@/components/Starfield";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.2, delay: 0.4 + i * 0.18, ease: EASE },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 1, delay: d, ease: EASE } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 pb-24 pt-28"
    >
      <div className="nebula nebula-a" />
      <div className="nebula nebula-b" />
      <div className="nebula nebula-c" />
      <Starfield />

      {/* portal rings */}
      <div
        aria-hidden="true"
        className="portal-pulse pointer-events-none absolute left-1/2 top-1/2 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full border border-violet-500/20"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[110vmin] w-[110vmin] -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-300/10"
      />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto max-w-4xl text-center">
        <motion.p
          variants={fadeUp}
          custom={0.15}
          initial="hidden"
          animate="show"
          className="eyebrow mb-8"
          data-testid="hero-eyebrow"
        >
          Source N Universe Presents
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display font-black uppercase leading-[0.95] tracking-[0.1em]"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              variants={lineReveal}
              custom={0}
              initial="hidden"
              animate="show"
              className="hero-glow-text block text-[clamp(3.4rem,15vw,8.5rem)]"
            >
              THE
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-3">
            <motion.span
              variants={lineReveal}
              custom={1}
              initial="hidden"
              animate="show"
              className="hero-glow-text block text-[clamp(3.4rem,15vw,8.5rem)]"
            >
              SHIFT
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeUp}
          custom={1.0}
          initial="hidden"
          animate="show"
          data-testid="hero-subheadline"
          className="font-quote mt-6 text-2xl italic text-slate-200 sm:text-3xl"
        >
          You were never meant to stay the same.
        </motion.p>

        <motion.p
          variants={fadeUp}
          custom={1.2}
          initial="hidden"
          animate="show"
          data-testid="hero-body"
          className="font-display mx-auto mt-8 max-w-xl text-xs leading-relaxed tracking-[0.25em] text-cyan-200/80 sm:text-sm"
        >
          WHAT IF THE REALITY YOU'RE LOOKING FOR… STARTS WITHIN YOU?
        </motion.p>

        <motion.p
          variants={fadeUp}
          custom={1.4}
          initial="hidden"
          animate="show"
          data-testid="hero-paragraph"
          className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
        >
          The Shift is a space for people who feel there's something more. Explore
          consciousness, energy, frequency, inner work, ancient wisdom, and the journey of
          becoming.
        </motion.p>

        <motion.div
          variants={fadeUp}
          custom={1.6}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-enter-shift-btn"
            onClick={() => scrollToId("#explore")}
            className="btn-portal"
          >
            Enter The Shift
            <Sparkles className="h-4 w-4" strokeWidth={2} />
          </button>
          <a
            data-testid="hero-join-community-btn"
            href="https://discord.gg/uvMNCcCYkx"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            Join The Community
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        variants={fadeUp}
        custom={2.0}
        initial="hidden"
        animate="show"
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#explore")}
        aria-label="Scroll to explore"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500 transition-colors duration-300 hover:text-cyan-300"
      >
        <ChevronDown className="scroll-hint h-6 w-6" strokeWidth={1.5} />
      </motion.button>
    </section>
  );
}
